import colorPallete, {
    bodyFonts,
    headlineFonts,
    labelFonts,
    themeFontWeights,
} from "@DSG/MobiusOverrides/styles/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { connect } from "react-redux";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";

const mapStateToProps = (state: ApplicationState) => {
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const orderSettings = getSettingsCollection(state).orderSettings;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session?.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session?.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;
    return {
        pageTitle: getCurrentPage(state).fields.title,
        isAuthenticated: state.context.session.isAuthenticated,
        displayVmiNavigation: currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),
    }
};

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    mainFooterGridContainer?: GridContainerProps;
    subFooterContainer?: GridContainerProps;
    subFooterMainContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    footerLinksWrapper?: InjectableCss;
    websiteLogoGridItem: GridItemProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    mainFooterGridContainer: {
        isFullWidth: true,
        offsetCss: css`
            justify-content: space-between;
        `,
        css: css`
            padding: 80px 40px;
            max-width: 1440px;

            > ${GridOffset} {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        flex-wrap: nowrap;
                    `,
                ], "min")}
            }

            .footer-link-list {
                .list-wrapper {
                    padding: 0;
                }
                .title-wrapper {
                    padding-bottom: 16px;
                    border-bottom: 1px solid ${colorPallete.grey400};
                    margin-bottom: 16px;
                }
                .links-wrapper {
                    line-height: ${labelFonts.label1Regular.lineHeight};
                    padding: 0;
                    a {
                        &:hover,
                        &:focus {
                            color: ${colorPallete.primaryRed500};
                            span {
                                color: ${colorPallete.primaryRed500};
                            }
                        }
                    }
                    &:not(:last-child) {
                        a {
                            margin-bottom: 16px;
                        }
                    }
                }
                .list-title {
                    font-size: ${bodyFonts.body1SemiBoldLg.fontSize};
                    line-height: ${bodyFonts.body1SemiBoldLg.lineHeight};
                    color: ${colorPallete.grey800};
                    font-weight: ${themeFontWeights.semiBold};
                    text-transform: none;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    font-size: ${bodyFonts.body2SemiBoldSm.fontSize};
                                    line-height: ${bodyFonts.body2SemiBoldSm.lineHeight};
                                `,
                                null,
                                null,
                            ],
                            "max",
                        );
                    }}
                }

                .list-link {
                    font-size: ${labelFonts.label1Regular.fontSize};
                    line-height: ${labelFonts.label1Regular.lineHeight};
                    color: ${colorPallete.grey800};
                    text-transform: capitalize;
                }

                @media screen and (max-width: 1023px) {
                    [class*="ContentWrapper-"] {
                        margin-top: 24px;
                        border-top: 1px solid ${colorPallete.grey400};
                        padding-top: 24px;

                        .app-images {
                            margin-bottom: 0;
                        }
                    }
                }
            }
            [class*="ContentWrapper-"] {
                h3 {
                    font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
                    line-height: ${headlineFonts.headline2SemiBoldLg.lineHeight};
                    font-weight: ${themeFontWeights.semiBold};
                }
                p {
                    font-size: ${labelFonts.label1Regular.fontSize};
                    line-height: ${labelFonts.label1Regular.lineHeight};
                    margin-bottom: 24px;
                }
                .app-images {
                    margin-bottom: 24px;
                    a {
                        &:first-child {
                            margin-right: 16px;
                        }
                        span {
                            display: flex;
                        }
                    }
                }
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                h3 {
                                    font-size: ${headlineFonts.headline3SemiBoldSm.fontSize};
                                    line-height: ${headlineFonts.headline3SemiBoldSm.lineHeight};
                                }
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 24px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    subFooterContainer: {
        isFullWidth: true,
        css: css`
            background: ${colorPallete.white};
            border-top: 1px solid ${colorPallete.grey200};
            &.vmiSubFooter {
                background: ${colorPallete.secondaryNavy500};
            }
            .mobile-logo {
                display: flex !important;
            }
            .subfooter-main-container {
                padding: 24px 80px;
                @media screen and (min-width: 1024px) and (max-width: 1366px) {
                    padding: 24px 40px;

                    .social-links {
                        flex-wrap: nowrap;
                    }
                }
                > div {
                    max-width: 100%;
                }
                .copyright-rte {
                    flex: 9;
                    max-width: 100%;
                }
                .social-links-block {
                    margin-right: 48px;
                }
                .social-links-block,
                .footer-site-logo {
                    flex: 1;
                    max-width: 100%;
                }
                .vmiFooterLogo {
                    max-width: 91px;
                }
                .footer-site-logo {
                    .logoWrapper > div {
                        display: flex;
                    }
                }
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                padding: 24px;
                                .footer-site-logo {
                                    order: 0;
                                }
                                .social-links-block {
                                    margin-right: 0px;
                                    order: 1;
                                    .social-links {
                                        justify-content: flex-end;
                                    }
                                }
                                .social-links-block,
                                .footer-site-logo {
                                    flex-basis: 50%;
                                }
                                .copyright-rte {
                                    order: 2;
                                    flex-basis: 100%;
                                    margin-top: 16px;
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    subFooterMainContainer: {
        css: css`
            div {
                align-items: center;
                > * {
                    padding: 0;
                }
            }
        `,
    },
    logoGridItem: {
        width: [12, 12, 12],
        css: css`
            width: 100%;
            max-width: 308px;
        `,
    },
    linkList1GridItem: {
        width: [12, 5, 4],
        css: css`
            min-width: 256px;
            &[class*="GridItemStyle-"] {
                &.footer-link-list {
                    @media screen and (max-width: 1023px) {
                        min-width: unset;
                    }
                    @media screen and (max-width: 390px) {
                        max-width: 100%;
                        flex-basis: 100%;
                    }
                    @media screen and (min-width: 768px) and (max-width: 1023px) {
                        flex-basis: 45.5%;
                        max-width: 45.5%;
                    }
                }
            }
        `,
    },
    linkList2GridItem: {
        width: [12, 5, 4],
        css: css`
            min-width: 256px;
            &[class*="GridItemStyle-"] {
                &.footer-link-list {
                    @media screen and (max-width: 1023px) {
                        margin-right: 0;
                        min-width: 163px;
                    }
                    @media screen and (max-width: 390px) {
                        max-width: 100%;
                        flex-basis: 100%;
                    }
                    @media screen and (min-width: 768px) and (max-width: 1023px) {
                        flex-basis: 45.5%;
                        max-width: 45.5%;
                    }
                }
            }
        `,
    },
    linkList3GridItem: {
        width: [12, 12, 8],
        css: css`
            min-width: 256px;
            @media screen and (min-width: 768px) and (max-width: 1023px) {
                flex-basis: 100%;
                max-width: 100%;
            }
        `,
    },
    subscribeGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
        `,
    },
    socialLinksGridItem: {
        css: css`
            .social-links {
                background: transparent;
                justify-content: flex-end;
                .link-wrapper {
                    margin-right: 0;
                    flex: none;
                    display: inline-flex;
                    &:not(:last-child) {
                        margin-right: 8px;
                    }
                }
            }
            &.vmiSocialLinks {

                * {
                    color: ${colorPallete.white};
                }
            }
        `,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        css: css`
            &.vmiCopyRight {
                .copyright-block {
                    * {
                        color: ${colorPallete.white};
                    }
                }
            }
            .copyright-block {
                p {
                    font-size: ${bodyFonts.body2RegularLg.fontSize};
                    line-height: ${bodyFonts.body2RegularLg.lineHeight};
                    color: ${colorPallete.grey800};
                    margin-bottom: 0;
                }
                a {
                    text-decoration: underline;
                }
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                p {
                                    font-size: ${labelFonts.label1Regular.fontSize};
                                    line-height: ${labelFonts.label1Regular.lineHeight};
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    footerLinksWrapper: {
        css: css`
            display: flex;
            gap: 40px;
            flex-wrap: wrap;

            @media only screen and (min-width: 1199px) {
                flex-wrap: nowrap;
            }
        `,
    },
    websiteLogoGridItem: {
        css: css`
            [class*="HiddenStyle-"] {
                display: block;
            }
        `,
    },
};

const FooterContainer = ({ id, pageTitle, isAuthenticated, displayVmiNavigation, extendedStyles }: Props) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    let isCheckoutPage = false;
    if (pageTitle === "Payment And Review") {
        isCheckoutPage = true;
    }

    return (
        <>
            {!displayVmiNavigation && (
                <>
                    {!isCheckoutPage && (
                        <GridContainer {...styles.widgetGridContainer} gap={0} isFullWidth={true}>
                            <GridItem {...styles.subscribeGridItem}>
                                <Zone zoneName="Subscribe" contentId={id} />
                            </GridItem>
                            <GridContainer className="main-footer-container" {...styles.mainFooterGridContainer} gap={0}>
                                <GridItem {...styles.logoGridItem} className="footer-intro">
                                    <StyledWrapper {...styles.logoWrapper}>
                                        <Zone zoneName="Logo" contentId={id} />
                                    </StyledWrapper>
                                </GridItem>
                                <StyledWrapper {...styles.footerLinksWrapper}>
                                    <GridItem {...styles.linkList1GridItem} className="footer-link-list">
                                        <Zone zoneName="LinkList1" contentId={id} />
                                    </GridItem>
                                    <GridItem {...styles.linkList2GridItem} className="footer-link-list">
                                        <Zone zoneName="LinkList2" contentId={id} />
                                    </GridItem>
                                    <GridItem {...styles.linkList3GridItem} className="footer-link-list">
                                        <Zone zoneName="LinkList3" contentId={id} />
                                    </GridItem>
                                </StyledWrapper>
                            </GridContainer>
                        </GridContainer>
                    )}
                    <GridContainer className="subfooter" gap={0} {...styles.subFooterContainer}>
                        <GridContainer className="subfooter-main-container" {...styles.subFooterMainContainer} gap={0}>
                            <GridItem {...styles.richContentGridItem} className="copyright-rte">
                                <Zone zoneName="RichContent" contentId={id} />
                            </GridItem>
                            <GridItem {...styles.socialLinksGridItem} className="social-links-block">
                                <Zone zoneName="SocialLinks" contentId={id} />
                            </GridItem>
                            <GridItem {...styles.websiteLogoGridItem} className="footer-site-logo">
                                <Zone zoneName="websiteLogo" contentId={id} />
                            </GridItem>
                        </GridContainer>
                    </GridContainer>
                </>
            )}
            {displayVmiNavigation && (
                <>
                    <GridContainer className="vmiSubFooter subfooter" gap={0} {...styles.subFooterContainer}>
                        <GridContainer className="subfooter-main-container" {...styles.subFooterMainContainer} gap={0}>
                            <GridItem {...styles.richContentGridItem} className="vmiCopyRight copyright-rte">
                                <Zone zoneName="VMIRichContent" contentId={id} />
                            </GridItem>
                            <GridItem {...styles.socialLinksGridItem} className="vmiSocialLinks social-links-block">
                                <Zone zoneName="VMISocialLinks" contentId={id} />
                            </GridItem>
                            <GridItem {...styles.websiteLogoGridItem} className="vmiFooterLogo footer-site-logo">
                                <Zone zoneName="VMIWebsiteLogo" contentId={id} />
                            </GridItem>
                        </GridContainer>
                    </GridContainer>
                </>
            )}
        </>
    );
};

const footerContainer: WidgetModule = {
    component: connect(mapStateToProps)(FooterContainer),
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
